import { BaseApiService } from '@/shared/api/BaseApiService';
import type {
  Appointment,
  CreateAppointmentInput,
  UpdateAppointmentInput,
  CancelAppointmentInput,
  SlotQueryParams,
  SlotAvailabilityResponse,
  AvailabilityResponse,
  AppointmentResponseDto,
  AppointmentDetailResponseDto,
  AppointmentListResponseDto,
  BookAppointmentInput,
} from '../domain/types';

export interface AppointmentListParams {
  date?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  providerId?: string;
  patientId?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'scheduled_at' | 'created_at' | 'status';
  sortOrder?: 'asc' | 'desc';
  tab?: 'today' | 'upcoming' | 'past' | 'cancelled';
}

export interface AppointmentListResponse {
  data: Appointment[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface AppointmentDetail extends Appointment {
  patientName: string | null;
  providerName: string | null;
  operatoryName: string | null;
  statusLog: Array<{
    id: string;
    appointmentId: string | null;
    eventType: string;
    previousStatus: string | null;
    newStatus: string | null;
    changedBy: string | null;
    changedAt: string;
    note: string | null;
  }>;
  scheduleHistory: Array<{
    id: string;
    previousScheduledAt: string;
    newScheduledAt: string;
    previousEndAt: string;
    newEndAt: string;
    previousDurationMinutes: number;
    newDurationMinutes: number;
    previousDoctorId: string | null;
    newDoctorId: string | null;
    previousOperatoryId: string | null;
    newOperatoryId: string | null;
    reason: string | null;
    note: string | null;
    changedBy: string | null;
    changedAt: string;
  }>;
  rescheduleCount: number;
}

export interface CreateAppointmentResult {
  appointment: Appointment;
  skipped?: string[];
}

export class AppointmentsService extends BaseApiService {
  protected readonly resourcePath = 'appointments';

  async list(params: AppointmentListParams = {}): Promise<AppointmentListResponse> {
    return this.get('', { params });
  }

  async getById(id: string): Promise<AppointmentDetail> {
    const detail = await this.get<AppointmentDetail>(`/${id}`);
    return {
      ...detail,
      scheduleHistory: detail.scheduleHistory ?? [],
      rescheduleCount: detail.rescheduleCount ?? 0,
      statusLog: detail.statusLog ?? [],
    };
  }

  async checkActiveAppointment(patientId: string): Promise<{ data: { id: string; status: string } | null }> {
    return this.get(`/active-for-patient/${patientId}`);
  }

  async create(
    input: CreateAppointmentInput,
    idempotencyKey?: string,
  ): Promise<CreateAppointmentResult> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey;
    }
    return this.post('', input, { headers });
  }

  async update(id: string, input: UpdateAppointmentInput, version: number): Promise<Appointment> {
    return this.patch(`/${id}`, input, { headers: { 'If-Match': String(version) } });
  }

  async confirm(id: string): Promise<Appointment> {
    return this.post(`/${id}/confirm`);
  }

  async checkIn(id: string): Promise<Appointment> {
    return this.post(`/${id}/check-in`);
  }

  async checkOut(id: string): Promise<Appointment> {
    return this.post(`/${id}/check-out`);
  }

  async cancel(id: string, input: CancelAppointmentInput): Promise<Appointment> {
    return this.post(`/${id}/cancel`, input);
  }

  async noShow(id: string): Promise<Appointment> {
    return this.post(`/${id}/no-show`);
  }

  async softDelete(id: string): Promise<void> {
    return this.post(`/${id}/delete`);
  }

  async getSlots(params: SlotQueryParams): Promise<SlotAvailabilityResponse> {
    return this.get('/slots', { params });
  }

  async todayQueue(): Promise<{ queue: Appointment[] }> {
    return this.get('/today/queue');
  }

  /* ── Track C methods ── */

  async getDoctorAvailability(
    doctorId: string,
    params: { date: string; procedureId?: string; durationOverride?: number; operatoryId?: string },
  ): Promise<AvailabilityResponse> {
    const { data } = await this.client.get(`/doctors/${doctorId}/availability`, { params });
    return data;
  }

  async bookAppointment(
    input: BookAppointmentInput,
    idempotencyKey?: string,
  ): Promise<AppointmentResponseDto> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey;
    }
    return this.post('/book', input, { headers });
  }

  async listTrackC(params: {
    date?: string;
    dateFrom?: string;
    dateTo?: string;
    doctorId?: string;
    status?: string[];
    limit?: number;
    offset?: number;
  }): Promise<AppointmentListResponseDto> {
    return this.get('/list', { params });
  }

  async getAppointmentDetail(id: string): Promise<AppointmentDetailResponseDto> {
    return this.get(`/detail/${id}`);
  }

  async startAppointment(id: string): Promise<AppointmentResponseDto> {
    return this.post(`/${id}/start`);
  }

  async completeAppointment(id: string): Promise<AppointmentResponseDto> {
    return this.post(`/${id}/complete`);
  }
}

export const appointmentsService = new AppointmentsService();
